import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import db from '../db/localdb';
import * as repo from '../sync/repo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';
import { lineTotal, lineTotalCents, toCents } from '../utils/money';

// Local (not UTC) YYYY-MM-DD for the <input type="date"> default.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function Sell() {
  const { business } = useAuth();
  const { toast }    = useToast();
  const navigate     = useNavigate();
  const [params]     = useSearchParams();

  const [products,  setProducts]  = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSug,    setShowSug]    = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [showProdSug,  setShowProdSug]  = useState(false);

  const [form, setForm] = useState({
    product_id: params.get('product') || '',
    customer_id: '',
    buyer_name: '', buyer_contact: '',
    qty: '1', unit_price: '',
    amount_paid: '', payment_status: 'paid',
    due_date: todayISO(),
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Product typeahead: only surface matches once the user starts typing —
  // no full dropdown on focus (keeps the form tidy).
  const pq = productQuery.trim().toLowerCase();
  const productSuggestions = pq
    ? products.filter(p => p.name.toLowerCase().includes(pq) || (p.sku || '').toLowerCase().includes(pq)).slice(0, 10)
    : [];

  const pickProduct = (p) => {
    setForm(f => ({ ...f, product_id: p.id }));
    setProductQuery(p.name);
    setShowProdSug(false);
  };

  // Pick an existing customer from the buyer-name typeahead — links the exact
  // customer id so repeat buyers always carry the same name and one rating.
  const pickCustomer = (c) => {
    setForm(f => ({ ...f, customer_id: c.id, buyer_name: c.name, buyer_contact: c.contact || '' }));
    setShowSug(false);
  };

  // Suggestions from the local customer list as the buyer name is typed.
  const q = form.buyer_name.trim().toLowerCase();
  const suggestions = q
    ? customers.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.contact || '').toLowerCase().includes(q)
      ).slice(0, 6)
    : [];
  // Don't show the list once the typed name exactly matches the linked customer.
  const linked = form.customer_id ? customers.find(c => c.id === form.customer_id) : null;
  const exactMatch = linked && linked.name.trim().toLowerCase() === q;

  // Read from the local Dexie mirror (populated by SyncManager) so the product
  // and customer pickers work offline — a network fetch here would leave the
  // dropdowns empty whenever the device is offline.
  useEffect(() => {
    const bid = business?.id;
    if (!bid) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const [pr, cr] = await Promise.all([
          db.products.where('business_id').equals(bid).filter(p => !p.voided_at).toArray(),
          db.customers.where('business_id').equals(bid).filter(c => !c.voided_at).toArray(),
        ]);
        if (!alive) return;
        pr.sort((a, b) => a.name.localeCompare(b.name));
        cr.sort((a, b) => a.name.localeCompare(b.name));
        setProducts(pr);
        setCustomers(cr);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [business?.id]);

  // Auto-fill price when product changes
  useEffect(() => {
    if (form.product_id) {
      const p = products.find(x => x.id === form.product_id);
      if (p) setForm(f => ({ ...f, unit_price: String(p.sale_price) }));
    }
  }, [form.product_id, products]);

  // Reflect a pre-selected product (e.g. ?product= from the inventory page)
  // in the search box once products have loaded.
  useEffect(() => {
    if (form.product_id && !productQuery) {
      const p = products.find(x => x.id === form.product_id);
      if (p) setProductQuery(p.name);
    }
  }, [form.product_id, products, productQuery]);

  // Auto-fill buyer from customer
  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find(x => x.id === form.customer_id);
      if (c) setForm(f => ({ ...f, buyer_name: c.name, buyer_contact: c.contact || '' }));
    }
  }, [form.customer_id, customers]);

  // Exact to the thebe — the same figure the server will store.
  const total = Number(lineTotal(form.qty, form.unit_price));
  // "Paid in full" means the whole total was received; only a partial payment
  // takes a typed amount. (Sending amount_paid 0 with 'paid' recorded every
  // cash sale as P0 received.)
  const paidStr = form.payment_status === 'paid' ? lineTotal(form.qty, form.unit_price)
    : form.payment_status === 'partial' ? (form.amount_paid || '0')
    : '0';

  // Stock control: block selling more than is on hand (services aren't tracked).
  const selectedProduct = products.find(x => x.id === form.product_id) || null;
  const tracksStock = selectedProduct && !selectedProduct.is_service;
  const available   = tracksStock ? Number(selectedProduct.stock_qty) : null;
  const qtyNum      = Number(form.qty) || 0;
  const overStock   = tracksStock && qtyNum > available;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id) {
      toast('Select a product from the list', 'error');
      return;
    }
    if (overStock) {
      toast(`Only ${available.toFixed(2)} in stock for ${selectedProduct.name}. Reduce the quantity.`, 'error');
      return;
    }
    if (form.payment_status !== 'paid' && !form.buyer_name.trim()) {
      toast('Buyer name is required for credit or partial sales', 'error');
      return;
    }
    if (!(Number(form.qty) > 0)) {
      toast('Enter a quantity greater than 0', 'error');
      return;
    }
    if (form.unit_price === '' || !(Number(form.unit_price) >= 0)) {
      toast('Enter a valid price', 'error');
      return;
    }
    if (form.payment_status === 'partial') {
      const paidCents = toCents(form.amount_paid);
      if (!(Number(form.amount_paid) > 0)) {
        toast('Enter the amount paid, or choose "Debt" if nothing was paid', 'error');
        return;
      }
      if (paidCents >= lineTotalCents(form.qty, form.unit_price)) {
        toast('That covers the full total — choose "Paid in full" instead', 'error');
        return;
      }
    }
    setSubmitting(true);
    try {
      const status = form.payment_status;
      const result = await repo.createSale({
        ...form,
        qty:            form.qty,
        unit_price:     form.unit_price,
        amount_paid:    paidStr,
        payment_status: status,
        due_date:       status === 'paid' ? null : (form.due_date || null),
        customer_id:    form.customer_id || null,
        // The real time of the sale — kept even if it syncs hours later.
        sold_at:        new Date().toISOString(),
      });
      toast(result._offline ? 'Sale queued (offline)' : 'Sale recorded', 'success');
      navigate('/dashboard');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to record sale', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Record Sale</h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group" style={{ position: 'relative' }}>
          <label>Product *</label>
          <input
            value={productQuery}
            onChange={(e) => { setProductQuery(e.target.value); setForm(f => ({ ...f, product_id: '' })); setShowProdSug(true); }}
            onFocus={() => setShowProdSug(true)}
            onBlur={() => setTimeout(() => setShowProdSug(false), 150)}
            autoComplete="off"
            required
            placeholder="Type to search a product…"
          />
          {selectedProduct && !selectedProduct.is_service && (
            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              In stock: {Number(selectedProduct.stock_qty).toFixed(2)}
            </div>
          )}
          {showProdSug && pq && (
            <ul style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
              margin: '3px 0 0', padding: 0, listStyle: 'none',
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              maxHeight: 240, overflowY: 'auto',
            }}>
              {productSuggestions.length === 0 && (
                <li className="text-muted" style={{ padding: '0.6rem 0.75rem' }}>No products match</li>
              )}
              {productSuggestions.map(p => (
                <li
                  key={p.id}
                  onMouseDown={() => pickProduct(p)}
                  style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}
                >
                  {p.name}
                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {p.is_service ? ' · service' : ` · stock ${Number(p.stock_qty).toFixed(2)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group">
            <label>Qty *</label>
            <input
              type="number" step="0.001" min="0.001"
              value={form.qty} onChange={set('qty')} required
              style={overStock ? { borderColor: 'var(--color-danger)' } : undefined}
            />
            {overStock && (
              <div style={{ color: 'var(--color-danger)', fontSize: '0.78rem', marginTop: '0.3rem' }}>
                Only {available.toFixed(2)} in stock — reduce the quantity.
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Unit price ({business?.currency}) *</label>
            <MoneyInput value={form.unit_price} onChange={set('unit_price')} required />
          </div>
        </div>

        {total > 0 && (
          <div className="card" style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{business?.currency} {new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(total)}</div>
          </div>
        )}

        <div className="form-group">
          <label>Customer (optional)</label>
          <select value={form.customer_id} onChange={set('customer_id')}>
            <option value="">Walk-in / no customer</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="form-group" style={{ position: 'relative' }}>
          <label>Buyer name {form.payment_status === 'paid' ? '(optional)' : '*'}</label>
          <input
            value={form.buyer_name}
            onChange={(e) => { setForm(f => ({ ...f, buyer_name: e.target.value, customer_id: '' })); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
            autoComplete="off"
            required={form.payment_status !== 'paid'}
            placeholder={form.payment_status === 'paid' ? 'e.g. John Banda (or leave blank for walk-in)' : 'e.g. John Banda'}
          />
          {showSug && !exactMatch && suggestions.length > 0 && (
            <ul style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
              margin: '3px 0 0', padding: 0, listStyle: 'none',
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              maxHeight: 220, overflowY: 'auto',
            }}>
              {suggestions.map(c => (
                <li
                  key={c.id}
                  onMouseDown={() => pickCustomer(c)}
                  style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}
                >
                  {c.name}
                  {c.contact && <span className="text-muted" style={{ fontSize: '0.8rem' }}> · {c.contact}</span>}
                </li>
              ))}
            </ul>
          )}
          {linked && exactMatch && (
            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              ✓ Linked to existing customer — keeps the same name & rating
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Contact</label>
          <input value={form.buyer_contact} onChange={set('buyer_contact')} placeholder="phone or email" />
        </div>

        <div className="form-group">
          <label>Payment status</label>
          <select value={form.payment_status} onChange={set('payment_status')}>
            <option value="paid">Paid in full</option>
            <option value="partial">Partial payment</option>
            <option value="debt">Debt (nothing paid yet)</option>
          </select>
        </div>

        {(form.payment_status === 'partial') && (
          <div className="form-group">
            <label>Amount paid ({business?.currency})</label>
            <MoneyInput value={form.amount_paid} onChange={set('amount_paid')} />
          </div>
        )}

        {(form.payment_status === 'partial' || form.payment_status === 'debt') && (
          <div className="form-group">
            <label>Due date</label>
            <input type="date" value={form.due_date} onChange={set('due_date')} />
          </div>
        )}

        <button type="submit" className="btn-primary" style={{ width: '100%', padding: '0.9rem' }} disabled={submitting || overStock}>
          {submitting ? 'Saving…' : overStock ? 'Not enough stock' : 'Record Sale'}
        </button>
      </form>
    </div>
  );
}
